import React, { useContext, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { OriginalUrlContext } from '../../utils/context';
import Paragraphs from '../../components/02_molecules/Paragraphs';
import HtmlHead from '../../components/01_atoms/HtmlHead';
import { GIFTS_COOKIEBOT_ID } from '../../utils/constants';

import bbTextStyles from '../../componentsGifts/BodyBlocks/BBText/index.module.scss';
import '../../styles/gifts/sass/theme.scss';

// Editors place <div id="CookieDeclaration"></div> in CMS HTML to opt the page
// into loading Cookiebot's declaration via the React component below.
const COOKIE_DECLARATION_PLACEHOLDER_RE =
  /<div\b[^>]*\bid\s*=\s*["']CookieDeclaration["'][^>]*>[\s\S]*?<\/div>/i;

const hasCookieDeclaration = (text = '') => COOKIE_DECLARATION_PLACEHOLDER_RE.test(text);

/**
 * Loads Cookiebot declaration outside dangerouslySetInnerHTML so React
 * re-renders do not wipe Cookiebot's injected UI.
 */
const CookieDeclaration = ({ nonce }) => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    if (!GIFTS_COOKIEBOT_ID || !nonce || !containerRef.current) {
      return undefined;
    }
    // CMS may still contain the placeholder div with this id
    // only skip if the nonced script itself is already present.
    if (document.querySelector('script#CookieDeclaration')) {
      return undefined;
    }

    const script = document.createElement('script');
    script.id = 'CookieDeclaration';
    script.src = `https://consent.cookiebot.com/${GIFTS_COOKIEBOT_ID}/cd.js`;
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('nonce', nonce);
    containerRef.current.appendChild(script);

    return undefined;
  }, [nonce]);

  return (
    <div className={`bb ${bbTextStyles['bb-text']}`}>
      <div className="container" ref={containerRef} />
    </div>
  );
};

CookieDeclaration.propTypes = {
  nonce: PropTypes.string,
};

const GiftsLandingPage = ({ entity: page, nonce = '' }) => {
  const originalUrl = useContext(OriginalUrlContext);
  const pathname = originalUrl.split('?')[0];

  let blocks = [...page.blocks];

  // Add "catalog" to "/corporate" page.
  if (pathname === '/corporate') {
    blocks.push({ blockType: 'gifts_corporate_catalog' });
  }

  // CMS placeholder div opts this page into the nonced Cookiebot declaration.
  const showCookieDeclaration = blocks.some(
    (block) => block.blockType === 'gifts_text_html' && hasCookieDeclaration(block.text),
  );

  return (
    <div>
      <HtmlHead metatags={page.metaTags} pageTitle={page.title} />
      <Paragraphs blocks={blocks} layout={'centered'} />
      {showCookieDeclaration && GIFTS_COOKIEBOT_ID && nonce && <CookieDeclaration nonce={nonce} />}
    </div>
  );
};

GiftsLandingPage.propTypes = {
  entity: PropTypes.object.isRequired,
  nonce: PropTypes.string,
};

export default GiftsLandingPage;
